/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box, Collapse, Typography } from '@mui/material';
import React from 'react';
import { AlertBanner } from '@opengeoweb/shared';
import type { AviationProduct } from '@opengeoweb/sigmet-airmet';
import {
  findSequenceNumber,
  getSequenceStyling,
} from '@opengeoweb/sigmet-airmet';
import {
  useFetchSigmetCancelTAC,
  useFetchAirmetCancelTAC,
  useFetchSigmetTAC,
  useFetchAirmetTAC,
} from '../../api/aviation-api/hooks';
import type { WarningType } from '../../store/warningsForm/types';
import { useWarningsTranslation } from '../../utils/i18n';

export const CancelAviationDialogContent = ({
  product,
  warningType,
  errorMessage,
}: {
  product: AviationProduct;
  warningType: WarningType;
  errorMessage?: string;
}): React.ReactElement => {
  const { t } = useWarningsTranslation();

  const isSigmet = warningType === 'sigmet';
  const enabled = !!product?.uuid;

  // Only run the two queries we actually need
  const sigCancel = useFetchSigmetCancelTAC(product, isSigmet && enabled);
  const airCancel = useFetchAirmetCancelTAC(product, !isSigmet && enabled);

  const sigCurrent = useFetchSigmetTAC(product, isSigmet && enabled);
  const airCurrent = useFetchAirmetTAC(product, !isSigmet && enabled);

  // Pick active queries per warning type
  const cancelQuery = isSigmet ? sigCancel : airCancel;
  const currentQuery = isSigmet ? sigCurrent : airCurrent;

  // Derived TAC strings
  const cancelTAC: string | null = cancelQuery.data?.data ?? null;
  const currentTAC: string | null = currentQuery.data?.data ?? null;

  // Per-section loading/error
  const isLoadingCancel = cancelQuery.isLoading || !enabled;
  const isLoadingCurrent = currentQuery.isLoading || !enabled;
  const isErrorCancel = !!cancelQuery.error;
  const isErrorCurrent = !!currentQuery.error;

  const loadingLabel = t('loading') ?? 'Loading...';
  const noTacLabel = t('tac-missing-data');

  const COLLAPSED = 90; // px

  const tacBlockStyle: React.CSSProperties = {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    overflowX: 'auto',
    overflowY: 'auto',
    maxWidth: '100%',
    fontFamily: 'monospace',
    fontSize: '0.9rem',
    lineHeight: 1.4,
  };

  const renderPre = (text: string): React.ReactElement => (
    <Box component="pre" sx={tacBlockStyle}>
      {text}
    </Box>
  );

  const renderTacWithSequence = (
    tac: string | null,
    noTacKey: string,
    prod?: AviationProduct,
  ): React.ReactElement => {
    if (!tac) {
      return (
        <Box component="pre" sx={tacBlockStyle}>
          {t(noTacKey)}
        </Box>
      );
    }

    const noTac = t(noTacKey);
    const hasTac = tac.split('\n').length > 0 && tac !== noTac;
    const sequenceNumberInTac = findSequenceNumber(tac, noTac);
    const sequenceStyle = getSequenceStyling(prod, hasTac);

    const words = tac.split(' ');

    return (
      <Box component="pre" sx={tacBlockStyle}>
        {words.map((word, i) => (
          <Box
            component="span"
            // eslint-disable-next-line react/no-array-index-key
            key={`${word}-${i}`}
            sx={hasTac && word === sequenceNumberInTac ? sequenceStyle : {}}
          >
            {word + (i < words.length - 1 ? ' ' : '')}
          </Box>
        ))}
      </Box>
    );
  };

  let cancelContent: React.ReactNode;
  if (isLoadingCancel) {
    cancelContent = renderPre(loadingLabel);
  } else if (isErrorCancel) {
    cancelContent = renderPre(noTacLabel);
  } else {
    cancelContent = renderTacWithSequence(cancelTAC, 'tac-missing-data');
  }

  let currentContent: React.ReactNode;
  if (isLoadingCurrent) {
    currentContent = renderPre(loadingLabel);
  } else if (isErrorCurrent) {
    currentContent = renderPre(noTacLabel);
  } else {
    currentContent = (
      <Box component="pre" sx={tacBlockStyle}>
        {currentTAC ?? noTacLabel}
      </Box>
    );
  }

  return (
    <Box sx={{ width: 500, maxWidth: 600 }}>
      <Typography fontWeight="medium" variant="inherit">
        {t('cancel-message')}
      </Typography>
      <div style={{ display: 'flex', paddingLeft: 12 }}>
        <Collapse collapsedSize={COLLAPSED} easing="ease" in timeout={200}>
          {cancelContent}
        </Collapse>
      </div>

      <Typography fontWeight="medium" variant="inherit">
        {t('will-cancel-message')}
      </Typography>
      <div style={{ display: 'flex', paddingLeft: 12 }}>
        <Collapse collapsedSize={COLLAPSED} easing="ease" in timeout={200}>
          {currentContent}
        </Collapse>
      </div>
      {errorMessage ? (
        <Box sx={{ marginBottom: 2 }}>
          <AlertBanner shouldClose title={errorMessage} />
        </Box>
      ) : null}
    </Box>
  );
};
